import React, { useState, useEffect } from 'react'
import axios from 'axios';
import {
    DataGrid,
    GridActionsCellItem,
    gridPageCountSelector,
    gridPageSelector,
    useGridApiContext,
    useGridSelector,
} from '@mui/x-data-grid';
import Pagination from '@mui/material/Pagination';
import { Box, Link, Button } from '@mui/material';
import { useSnackbar } from 'notistack';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CloudUploadIcon from '@mui/icons-material/CloudUploadOutlined';
import { darken, lighten } from '@mui/material/styles';
import { renderCellExpand } from './RenderCellExpand'
import { useAuth } from './utils/Auth';
import UploadDocuments from './UploadDocuments';
import { editTextArea } from './EditTextarea';
import GridCustomToolbar from './GridCustomToolbar';
import './Data.css';

const getBackgroundColor = (color, mode) =>
    mode === 'dark' ? darken(color, 0.6) + '!important' : lighten(color, 0.6) + '!important';

const getHoverBackgroundColor = (color, mode) =>
    mode === 'dark' ? darken(color, 0.4) + '!important' : lighten(color, 0.4) + '!important';

const customRowStyle = {
    '& .super-app-theme--Accepted': {
        bgcolor: (theme) =>
            getBackgroundColor(theme.palette.success.main, theme.palette.mode),
        '&:hover': {
            bgcolor: (theme) =>
                getHoverBackgroundColor(
                    theme.palette.success.main,
                    theme.palette.mode,
                ),
        },
    },
    '& .super-app-theme--Rejected': {
        bgcolor: (theme) =>
            getBackgroundColor(theme.palette.error.main, theme.palette.mode),
        '&:hover': {
            bgcolor: (theme) =>
                getHoverBackgroundColor(theme.palette.error.main, theme.palette.mode),
        },
    },
    '& .super-app-theme--Closed': {
        bgcolor: (theme) =>
            getBackgroundColor(theme.palette.warning.main, theme.palette.mode),
        '&:hover': {
            bgcolor: (theme) =>
                getHoverBackgroundColor(
                    theme.palette.warning.main,
                    theme.palette.mode,
                ),
        },
    },
    '& .super-app-theme--QueryToLocation ': {
        bgcolor: (theme) =>
            getBackgroundColor(theme.palette.info.main, theme.palette.mode),
        '&:hover': {
            bgcolor: (theme) =>
                getHoverBackgroundColor(theme.palette.info.main, theme.palette.mode),
        },
    },
    '& .super-app-theme--Responded': {
        bgcolor: (theme) =>
            getBackgroundColor(theme.palette.secondary.main, theme.palette.mode),
        '&:hover': {
            bgcolor: (theme) =>
                getHoverBackgroundColor(theme.palette.secondary.main, theme.palette.mode),
        },
    },
    '& .MuiDataGrid-root .MuiDataGrid-row.Mui-selected': {
        backgroundColor: '#c3ea0adb !important',
        color: 'primary',
        '&:hover': {
            backgroundColor: 'yellow !important'
        }
    }

}

export default function Data() {
    const [initialData, setInitialData] = useState([]);
    const [rowData, setRowData] = useState([]);
    const [dataset, setDataset] = useState([]);
    const [files, setFiles] = useState([]);
    const [showMenuSelected, setShowMenuSelected] = useState('All');
    const [editedRows, setEditedRows] = useState([]);
    const { enqueueSnackbar } = useSnackbar();

    let filterDataID = {};
    const auth = useAuth();
    const url = "https://inlinetracking.com/wp-json/inline-tracking/v1/route/records/";

    const [openModal, setOpenModal] = useState(false);
    const [rowID, setRowID] = useState();
    const [entryID, setEntryID] = useState();
    const [render, setRender] = useState(false);

    const handleOpenModal = (row) => () => {
        setRowID(row.id);
        setEntryID(row.entry);
        setOpenModal(true);
    }

    const handleCloseModal = () => {
        setFiles([]);
        setOpenModal(false);
    };

    const updateLocationEvidence = (id, uploads) => {
        setRowData((prevRows) => {
            const rowToUpdate = prevRows.find((row) => row.id === id);
            rowToUpdate['locationEvidence'] = uploads
            return [...prevRows];
        });
        setDataset((prevRows) => {
            const rowToUpdate = prevRows.find((row) => row.id === id);
            rowToUpdate['locationEvidence'] = uploads
            return [...prevRows];
        });
    };

    const filteringRowData = (newValue) => {
        initialData.forEach((row) => {
            switch (newValue) {
                case 'All':
                    filterDataID[row.id] = true;
                    break;

                case 'Outstanding':
                    filterDataID[row.id] = ['Closed', 'Accepted', 'Rejected'].indexOf(row.status) === -1 ? true : false;
                    break;

                default:
                    filterDataID[row.id] = row.status === showMenuSelected ? true : false;
                    break;
            }
        });
    }

    useEffect(() => {
        let nonce = typeof Window.nonce !== 'undefined' ? Window.nonce : auth.nonce;

        axios.get(url, { headers: { "X-WP-Nonce": nonce } })
            .then(response => response.data)
            .then(data => {
                setDataset(JSON.parse(JSON.stringify(data)));
                setInitialData(data);
            })
            .catch(error => {
                enqueueSnackbar("Your session is expired  ", { variant: 'info' });
                auth.logout();
            });

    }, [render]);

    useEffect(() => {

        filteringRowData(showMenuSelected);

        const newArray = [];

        dataset.forEach((row) => {
            if (filterDataID[row.id])
                newArray.push(row);
        });

        setRowData(newArray);

    }, [initialData, showMenuSelected]);

    const columns = [
        { field: 'id', headerName: 'ID', type: 'number', minWidth: 80, align: 'center', headerAlign: 'center' },
        { field: 'customer', headerName: 'Customer', minWidth: 150, align: 'center', headerAlign: 'center' },
        { field: 'dateOfQuery', headerName: 'Date Of Query', type: 'date', flex: 0.1, hide: true, renderCell: renderCellExpand, minWidth: 150, },
        { field: 'deliveryDate', headerName: 'Delivery Date', type: 'date', flex: 0.1, hide: true, renderCell: renderCellExpand, minWidth: 150, },
        { field: 'deliveryToDc', headerName: 'Delivery To DC', flex: 0.1, renderCell: renderCellExpand, minWidth: 150, },
        { field: 'deliveryToSection', headerName: 'Delivery To Section', flex: 0.1, renderCell: renderCellExpand, minWidth: 170 },
        { field: 'tradingPartner', headerName: 'Trading Partner', flex: 0.1, renderCell: renderCellExpand, minWidth: 200 },
        { field: 'customerReferenceNumber', headerName: 'Customer Reference Number', flex: 0.1, renderCell: renderCellExpand, minWidth: 210 },
        { field: 'conNote', headerName: 'ConNote', flex: 0.1, renderCell: renderCellExpand, minWidth: 120 },
        { field: 'transferDocketNumber', headerName: 'Docket Number', flex: 0.1, renderCell: renderCellExpand, minWidth: 160 },
        {
            field: 'equipmentType', headerName: 'Equipment Type', flex: 0.18, type: 'string', minWidth: 180, align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div>
                        {params.value.map((element, index) => (
                            <div key={index}> {element} </div>
                        ))}
                    </div>
                );
            },

        },
        {
            field: 'sentQty', headerName: 'Sent QTY', flex: 0.08, type: 'number', minWidth: 100, align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div>
                        {params.value.map((element, index) => (
                            <div key={index}> {element} </div>
                        ))}
                    </div>
                );
            },
        },
        { field: 'queryType', headerName: 'Query Type', flex: 0.1, renderCell: renderCellExpand, minWidth: 200 },
        { field: 'furtherDetails', headerName: 'Further Details', flex: 0.1, renderCell: renderCellExpand, minWidth: 200 },
        {
            field: 'evidence', headerName: 'TP Evidence', flex: 0.1, minWidth: 100, align: 'center', headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div>
                        {params.value.map((element, index) => (
                            <Link key={index} href={element} target="_blank"> View </Link>
                        ))}
                    </div>
                );
            }
        },
        {
            field: 'iltNotes', headerName: 'ILT Notes for Location', flex: 0.1,
            editable: (auth.editable >= 100 && auth.editable <= 199) ? true : false, hide: (auth.editable >= 300 && auth.editable <= 399) ? true : false,
            renderCell: renderCellExpand, renderEditCell: editTextArea, minWidth: 200,
        },
        {
            field: 'comments', headerName: 'Location Comments', flex: 0.1,
            editable: (auth.editable >= 100 && auth.editable <= 299) ? true : false, hide: (auth.editable >= 300 && auth.editable <= 399) ? true : false,
            renderCell: renderCellExpand, renderEditCell: editTextArea, minWidth: 200
        },
        {
            field: 'locationEvidence', headerName: 'Location Evidence', flex: 0.1, minWidth: 140, align: 'center', headerAlign: 'center',
            hide: (auth.editable >= 300 && auth.editable <= 399) ? true : false,
            renderCell: (params) => {
                return (
                    <div>
                        {params.value.map((element, index) => (
                            <Link key={index} href={element} target="_blank" > View </Link>
                        ))}
                    </div>
                );
            }
        },
        { field: 'submittedBy', headerName: 'Submitted By', flex: 0.1, hide: true, renderCell: renderCellExpand, minWidth: 120 },
        {
            field: 'email', headerName: 'Email Address', flex: 0.1, hide: true, minWidth: 200,
            renderCell: renderCellExpand
        },
        {
            field: 'finalComments', headerName: 'Final Comments for TP', flex: 0.1,
            editable: (auth.editable >= 100 && auth.editable <= 199) ? true : false,
            renderCell: renderCellExpand, renderEditCell: editTextArea, minWidth: 200,
        },
        {
            field: 'status', headerName: 'Status', type: 'singleSelect', flex: 0.1,
            editable: (auth.editable >= 100 && auth.editable <= 299) ? true : false, renderCell: renderCellExpand, minWidth: 150,
            valueOptions: () => {
                if (auth.editable >= 100 && auth.editable <= 199) {
                    return ['Accepted', 'Closed', 'Query To Location', 'Rejected', 'Responded'];
                }
                return ['Query To Location', 'Responded'];
            },
            preProcessEditCellProps: (params) => {
                let errorMessage = false;
                if (params.row.iltNotes.length === 0) {
                    errorMessage = true;
                    enqueueSnackbar("ILT Notes cannot be emptied. Please enter the comments before selecting the status.", { variant: 'error', autoHideDuration: 5000 });
                }
                else if (params.props.value === 'Responded' && params.row.comments.length === 0) {
                    errorMessage = true;
                    enqueueSnackbar("Location Comments cannot be emptied. Please enter the comments before selecting the status.", { variant: 'error', autoHideDuration: 5000 });
                }
                else if (['Accepted', 'Closed', 'Rejected'].includes(params.props.value) && params.row.finalComments.length === 0) {
                    errorMessage = true;
                    enqueueSnackbar("Final Comments cannot be emptied. Please enter the final comments before selecting the status.", { variant: 'error', autoHideDuration: 5000 });
                }
                return { ...params.props, error: errorMessage }
            }
        },
        {
            field: 'actions', type: 'actions', headerName: "Actions", width: 250,
            hide: (auth.editable >= 300 && auth.editable <= 399) ? true : false,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<Button variant="contained" size='small' component="label" title="Upload Evidence"
                        disabled={(params.row.status === 'Accepted' || params.row.status === 'Rejected' || params.row.status === 'Closed') || params.row.locationEvidence.length !== 0}>
                        <CloudUploadIcon />
                    </Button>}
                    label="Upload"
                    disabled={(params.row.status === 'Accepted' || params.row.status === 'Rejected' || params.row.status === 'Closed') || params.row.locationEvidence.length !== 0}
                    onClick={handleOpenModal(params.row)}
                />,
                <GridActionsCellItem
                    icon={<Button variant="contained" size='small' component="label" title="Click me to save"
                        disabled={!editedRows.includes(params.row.id)}>
                        <SaveOutlinedIcon />
                    </Button>}
                    label="Save"
                    disabled={!editedRows.includes(params.row.id)}
                    onClick={e => saveRow(params.row, e)}
                />,
                <GridActionsCellItem
                    icon={<Button variant="contained" size='small' component="label" title="Send the Status of the Claim to TP"
                        disabled={
                            !(
                                (auth.editable >= 100 && auth.editable <= 199) &&
                                (params.row.status === 'Accepted' || params.row.status === 'Rejected' || params.row.status === 'Closed')
                            )
                        }>
                        {params.row.emailSent ?
                            <CheckCircleOutlineOutlinedIcon /> :
                            <ForwardToInboxOutlinedIcon />}
                    </Button >}
                    label="Email to TP"
                    disabled={
                        params.row.emailSent ||
                        !(
                            (auth.editable >= 100 && auth.editable <= 199) &&
                            (params.row.status === 'Accepted' || params.row.status === 'Rejected' || params.row.status === 'Closed')
                        )
                    }
                    onClick={sendEmail(params.row)}
                />
            ]
        },
    ];

    const saveRow = (row, e) => {
        e.stopPropagation();
        let sendData = {
            status: row.status,
            comments: row.comments,
            iltNotes: row.iltNotes,
            finalComments: row.finalComments,
            entry: row.entry,
        }

        let nonce = typeof Window.nonce !== 'undefined' ? Window.nonce : auth.nonce;
        axios.put(url + row.id, sendData, { headers: { "X-WP-Nonce": nonce } })
            .then(response => response.data)
            .then((data) => {
                enqueueSnackbar(data, { variant: 'success' });
                setEditedRows((prevEditedRows) => prevEditedRows.filter((rowId) => rowId !== row.id));
                setRender(!render);
            })
            .catch(error => {
                enqueueSnackbar("Error is encountered:  " + error.response.data.message, { variant: 'error' });
            });
    };

    const sendEmail = (row) => () => {

        if (row.finalComments.length === 0) {
            enqueueSnackbar("Final Comments for TP cannot be emptied. Please enter it", { variant: 'error' });
            return;
        }

        const sendingData = {
            status: row.status,
            comments: row.comments,
            iltNotes: row.iltNotes,
            finalComments: row.finalComments,
            entry: row.entry,
        }

        let nonce = typeof Window.nonce !== 'undefined' ? Window.nonce : auth.nonce;
        axios.put(url + row.id + '/send-email', sendingData, { headers: { "X-WP-Nonce": nonce } })
            .then(response => response.data)
            .then((data) => {
                enqueueSnackbar(data, { variant: 'success' });
                setEditedRows((prevEditedRows) => prevEditedRows.filter((rowId) => rowId !== row.id));
                setRender(!render);
            })
            .catch(error => {
                enqueueSnackbar("Error is encountered:  " + error.response.data.message, { variant: 'error' });
            });
    };

    const handleEditCommit = (values) => {
        let oldValue = '';
        if (values.field === 'status') {
            const foundRow = rowData.find((row) => row.id === values.id);
            oldValue = foundRow.status;
        }
        else
            oldValue = values.row[values.field];

        if (!editedRows.includes(values.id) && values.value !== oldValue)
            setEditedRows([...editedRows, values.id]);

        setDataset((prevRows) => {
            const rowToUpdate = prevRows.find((row) => row.id === values.id);
            rowToUpdate[values.field] = values.value;
            return [...prevRows];
        });
    };

    const handleEditable = (params) => {
        const foundRow = initialData.find((row) => row.id === params.id);
        if (params.field === 'finalComments')
            return true;
        return ['Closed', 'Accepted', 'Rejected'].indexOf(foundRow.status) === -1 ? true : false;
    }

    const handleRowSelectable = (params) => {
        const foundRow = initialData.find((row) => row.id === params.id);
        return ['Closed', 'Accepted', 'Rejected'].indexOf(foundRow.status) === -1 ? true : false;
    }

    return (
        <>
            <UploadDocuments rowID={rowID} entryID={entryID} openModal={openModal} setOpenModal={setOpenModal}
                handleCloseModal={handleCloseModal} files={files} setFiles={setFiles} updateLocationEvidence={updateLocationEvidence} />
            <div style={{ width: '100%', height: 650 }}>
                <div style={{ display: 'flex', height: '100%' }}>
                    <Box
                        style={{ flexGrow: 1 }}
                        sx={customRowStyle}
                    >
                        <DataGrid
                            rows={rowData}
                            columns={columns}
                            autoPageSize
                            pagination
                            checkboxSelection
                            disableSelectionOnClick
                            localeText={{
                                toolbarDensity: 'Size',
                                toolbarDensityLabel: 'Size',
                                toolbarDensityCompact: 'Small',
                                toolbarDensityStandard: 'Medium',
                                toolbarDensityComfortable: 'Large',
                            }}
                            components={{
                                Toolbar: GridCustomToolbar,
                                Pagination: CustomPagination,
                            }}
                            componentsProps={{
                                toolbar: {
                                    showMenuSelected: showMenuSelected,
                                    setShowMenuSelected: setShowMenuSelected
                                }
                            }}
                            getRowHeight={() => 'auto'}
                            isCellEditable={(params) => handleEditable(params)}
                            isRowSelectable={(params) => handleRowSelectable(params)}
                            onCellEditCommit={(values) => { handleEditCommit(values); }}
                            sx={{
                                boxShadow: 2,
                                border: 2,
                                borderColor: 'primary.light',
                                '& .MuiDataGrid-cell:hover': {
                                    color: 'primary.main',
                                },
                            }}
                            getRowClassName={(params) => {
                                const statusString = params.row.status.replace(/ /g, "");
                                return `super-app-theme--${statusString}`;
                            }}
                        />
                    </Box>
                </div>
            </div>
        </>
    )
}

function CustomPagination() {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <Pagination
            color="primary"
            count={pageCount}
            page={page + 1}
            onChange={(event, value) => apiRef.current.setPage(value - 1)}
        />
    );
}
